import { throttle } from 'es-toolkit'
import { useEffect, useMemo, useRef, useState } from 'react'

import PriceWebSocket from '@/libs/reliable-websocket/PriceWebSocket'
import type { RawCoinMarketMessage } from '@/libs/reliable-websocket/PriceWebSocket/utils'
import { transformRawCoinMarketMessage } from '@/libs/reliable-websocket/PriceWebSocket/utils'
import type { CoinSlug, LiveCoinMarket } from '@/types/coin'

import useCoinStore from '../_store'

export default function useSubscribeLiveCoinMarket(
  slug: CoinSlug,
  isPriceAlive: boolean,
  throttleWait = 1000,
) {
  const wsRef = useRef<PriceWebSocket | null>(null)
  const [wsCreated, setWsCreated] = useState(false)
  const isMountedRef = useRef(true)

  // Always keep a live ws connection for coin page
  useEffect(() => {
    isMountedRef.current = true
    wsRef.current = new PriceWebSocket()
    setWsCreated(true)
    return () => {
      isMountedRef.current = false
      wsRef.current?.close()
    }
  }, [])

  const updateLiveCoinMarketThrottled = useMemo(
    () =>
      throttle((liveCoinMarket: LiveCoinMarket) => {
        if (isMountedRef.current) {
          useCoinStore.setState({ liveCoinMarket })
        }
      }, throttleWait),
    [throttleWait],
  )

  useEffect(() => {
    if (!slug || !isPriceAlive || !wsRef.current) return

    wsRef.current.subscribe<RawCoinMarketMessage>({ slugs: [slug] }, rawCoinMarketMessage => {
      if (!rawCoinMarketMessage || !rawCoinMarketMessage.slug) return

      const newLiveCoinMarket: LiveCoinMarket = {
        ...useCoinStore.getState().liveCoinMarket,
        ...transformRawCoinMarketMessage(rawCoinMarketMessage),
      }
      updateLiveCoinMarketThrottled(newLiveCoinMarket)
    })

    return () => {
      useCoinStore.getState().clear()
      wsRef.current?.unsubscribe()
    }
  }, [slug, isPriceAlive, updateLiveCoinMarketThrottled, wsCreated])
}
